import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// Mythidverse Unity Component - Updated for real Unity integration
const MythidverseViewer = ({ product, onClose }) => {
  const unityContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unityReady, setUnityReady] = useState(false);
  const [objectLoading, setObjectLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    // Create iframe to load Unity
    const iframe = document.createElement('iframe');
    iframe.src = '/Build/index.html'; // Path to your Unity build
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.background = '#000';
    
    iframeRef.current = iframe;
    
    // Handle iframe load
    iframe.onload = () => {
      console.log('Unity iframe loaded');
      
      // Wait a bit for Unity to initialize
      setTimeout(() => {
        if (isMounted) {
          setIsLoading(false);
          setUnityReady(true);
          
          // Send object ID to Unity
          const objectId = getUnityObjectId(product);
          console.log('Sending object to Unity:', objectId);
          
          // Try to send message to Unity
          try {
            if (iframe.contentWindow && iframe.contentWindow.showObjectInMythidverse) {
              setObjectLoading(true);
              iframe.contentWindow.showObjectInMythidverse(objectId);
            } else {
              // Fallback: try accessing Unity instance directly
              setTimeout(() => {
                if (iframe.contentWindow && iframe.contentWindow.unityInstance) {
                  iframe.contentWindow.unityInstance.SendMessage('MythidverseManager', 'ShowObjectOnTable', objectId);
                  setObjectLoading(false);
                }
              }, 1000);
            }
          } catch (err) {
            console.error('Error communicating with Unity:', err);
            setError('Failed to load object in 3D viewer');
          }
        }
      }, 2000);
    };
    
    iframe.onerror = () => {
      if (isMounted) {
        setError('Failed to load Unity WebGL. Make sure the build is deployed correctly.');
        setIsLoading(false);
      }
    };
    
    // Message listener for Unity events
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      
      const data = event.data;
      if (data.type === 'unityObjectLoaded') {
        setObjectLoading(false);
        console.log('Unity: Object loaded successfully');
      } else if (data.type === 'unityObjectFailed') {
        setObjectLoading(false);
        setError(`Failed to load object: ${data.error}`);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Append iframe to container
    if (unityContainerRef.current) {
      unityContainerRef.current.appendChild(iframe);
    }
    
    return () => {
      isMounted = false;
      window.removeEventListener('message', handleMessage);
      
      if (unityContainerRef.current && iframe && unityContainerRef.current.contains(iframe)) {
        unityContainerRef.current.removeChild(iframe);
      }
    };
  }, [product]);

  // Map product data to Unity object IDs
  const getUnityObjectId = (product) => {
    // Direct mapping if available
    if (product.unityObjectId) {
      return product.unityObjectId;
    }
    
    // Use model path if available
    if (product.model && product.model.path) {
      // Extract filename without extension from the path
      const filename = product.model.path.split('/').pop().split('.')[0];
      return filename.replace(/[^a-zA-Z0-9]/g, '_');
    }
    
    // Map specific products to Unity prefab names
    const mapping = {
      'Galactic Voyager X-7': 'voyager_spaceship',
      '1957 Quantum Roadster': 'vintage_car',
      'Ultra Guardian Action Figure': 'action_figure',
      'Wind-Up Mechanical Dinosaur': 'wind_up_toy',
      'Nebula Cruiser Deluxe': 'nebula_cruiser'
    };
    
    if (mapping[product.title]) {
      return mapping[product.title];
    }
    
    // Fallback: convert title to Unity-compatible ID
    return product.title.toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // Handle rotation toggle
  const handleToggleRotation = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        if (iframeRef.current.contentWindow.unityInstance) {
          iframeRef.current.contentWindow.unityInstance.SendMessage('MythidverseManager', 'ToggleRotation');
        }
      } catch (err) {
        console.error('Error toggling rotation:', err);
      }
    }
  };

  // Handle clear object
  const handleClearObject = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        if (iframeRef.current.contentWindow.unityInstance) {
          iframeRef.current.contentWindow.unityInstance.SendMessage('MythidverseManager', 'ClearCurrentObject');
        }
      } catch (err) {
        console.error('Error clearing object:', err);
      }
    }
  };
  
  return (
    <div className="mythidverse-viewer">
      <div className="mythidverse-controls">
        {unityReady && !objectLoading && !error && (
          <>
            <button onClick={handleToggleRotation}>
              Toggle Rotation
            </button>
            <button onClick={handleClearObject}>
              Clear Object
            </button>
          </>
        )}
        <button onClick={onClose}>Close Mythidverse</button>
      </div>
      
      <div className="mythidverse-container" ref={unityContainerRef}>
        {(isLoading || objectLoading) && !error && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">
              {isLoading ? 'Loading Mythidverse...' : 'Loading 3D Object...'}
            </div>
          </div>
        )}
        
        {error && (
          <div className="error-overlay">
            <div className="error-icon">⚠️</div>
            <div className="error-text">{error}</div>
            <div className="error-details">
              <p>Troubleshooting steps:</p>
              <ul>
                <li>Check that Unity WebGL build is in /public/Build/</li>
                <li>Verify build files are named correctly</li>
                <li>Make sure prefab '{getUnityObjectId(product)}' exists in Resources/Models/</li>
              </ul>
            </div>
            <button onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        )}
      </div>
      
      <div className="mythidverse-info">
        <h3>{product.title}</h3>
        <p>{product.price} ETH</p>
        <p className="mythidverse-description">
          {unityReady ? 'Viewing in Mythidverse 3D Environment' : 'Loading 3D Environment...'}
        </p>
        <div className="model-info">
          {product.model && (
            <span className="model-badge">
              {product.model.type.toUpperCase()} Model
            </span>
          )}
          <span className="unity-id">
            Unity ID: {getUnityObjectId(product)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Original NFTCard3D Component (unchanged)
const NFTCard3D = ({ product, onClose }) => {
  const mountRef = useRef(null);
  const [isRotating, setIsRotating] = useState(true);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const controlsRef = useRef(null);
  const mixerRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());

  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0f);
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.z = 5;
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(400, 400);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountRef.current.appendChild(renderer.domElement);
    
    // Add orbit controls for better interaction
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.enableZoom = true;
    controls.enablePan = false;
    controls.autoRotate = isRotating;
    controls.autoRotateSpeed = 3;
    controlsRef.current = controls;
    
    // Lighting setup
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    // Add directional light with shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    scene.add(directionalLight);
    
    // Add colored point lights for the cyberpunk effect
    const blueLight = new THREE.PointLight(0x00f3ff, 1, 10);
    blueLight.position.set(5, 5, 5);
    scene.add(blueLight);
    
    const pinkLight = new THREE.PointLight(0xff00aa, 1, 10);
    pinkLight.position.set(-5, -5, 5);
    scene.add(pinkLight);

    // Model or image display logic
    let object3D = null;

    // Check if the product has a 3D model
    if (product.model) {
      // Handle 3D model based on type
      const modelPath = product.model.path || '';
      
      if (product.model.type === 'fbx') {
        // Load FBX model
        const loader = new FBXLoader();
        loader.load(
          modelPath,
          (fbx) => {
            // Apply scale from product data or default
            const scale = product.model.scale || 0.01;
            fbx.scale.set(scale, scale, scale);
            
            // Apply position if specified
            if (product.model.position) {
              const [x, y, z] = product.model.position;
              fbx.position.set(x, y, z);
            }
            
            // Apply rotation if specified
            if (product.model.rotation) {
              const [x, y, z] = product.model.rotation;
              fbx.rotation.set(
                THREE.MathUtils.degToRad(x),
                THREE.MathUtils.degToRad(y),
                THREE.MathUtils.degToRad(z)
              );
            }
            
            // Setup animations if available
            if (fbx.animations && fbx.animations.length) {
              const mixer = new THREE.AnimationMixer(fbx);
              const action = mixer.clipAction(fbx.animations[0]);
              action.play();
              mixerRef.current = mixer;
            }
            
            // Enable shadows
            fbx.traverse((child) => {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
              }
            });
            
            scene.add(fbx);
            object3D = fbx;
            setModelLoaded(true);
          },
          (xhr) => {
            setLoadingProgress(Math.floor((xhr.loaded / xhr.total) * 100));
          },
          (error) => {
            console.error('Error loading FBX model:', error);
            // Fallback to default cube if model fails to load
            createDefaultCube();
          }
        );
      } else if (product.model.type === 'obj') {
        // Load OBJ model
        const loader = new OBJLoader();
        loader.load(
          modelPath,
          (obj) => {
            // Apply scale from product data or default
            const scale = product.model.scale || 0.01;
            obj.scale.set(scale, scale, scale);
            
            // Apply position if specified
            if (product.model.position) {
              const [x, y, z] = product.model.position;
              obj.position.set(x, y, z);
            }
            
            // Apply rotation if specified
            if (product.model.rotation) {
              const [x, y, z] = product.model.rotation;
              obj.rotation.set(
                THREE.MathUtils.degToRad(x),
                THREE.MathUtils.degToRad(y),
                THREE.MathUtils.degToRad(z)
              );
            }
            
            // Enable shadows
            obj.traverse((child) => {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
              }
            });
            
            scene.add(obj);
            object3D = obj;
            setModelLoaded(true);
          },
          (xhr) => {
            setLoadingProgress(Math.floor((xhr.loaded / xhr.total) * 100));
          },
          (error) => {
            console.error('Error loading OBJ model:', error);
            // Fallback to default cube if model fails to load
            createDefaultCube();
          }
        );
      } else {
        // Unknown model type, fallback to image cube
        createDefaultCube();
      }
    } else {
      // No 3D model, use the default image cube
      createDefaultCube();
    }

    // Default cube creation function (for fallback or image display)
    function createDefaultCube() {
      const geometry = new THREE.BoxGeometry(3, 3, 0.2);
      
      // Create texture loader and load the NFT image
      const textureLoader = new THREE.TextureLoader();
      const texture = textureLoader.load(product.image, () => {
        texture.needsUpdate = true;
        setModelLoaded(true);
      });
      
      // Create materials with the NFT texture on the front and a stylized back
      const materials = [
        new THREE.MeshBasicMaterial({ color: 0x555555 }), // Right
        new THREE.MeshBasicMaterial({ color: 0x555555 }), // Left
        new THREE.MeshBasicMaterial({ color: 0x555555 }), // Top
        new THREE.MeshBasicMaterial({ color: 0x555555 }), // Bottom
        new THREE.MeshBasicMaterial({ map: texture }), // Front
        new THREE.MeshBasicMaterial({ color: 0x292929 }) // Back
      ];
      
      // Create the cube with the materials
      const cube = new THREE.Mesh(geometry, materials);
      cube.castShadow = true;
      cube.receiveShadow = true;
      scene.add(cube);
      object3D = cube;
    }
    
    // Add a floor for shadow casting if showing 3D models
    if (product.model) {
      const floorGeometry = new THREE.PlaneGeometry(10, 10);
      const floorMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x111111,
        roughness: 0.8,
        metalness: 0.2
      });
      const floor = new THREE.Mesh(floorGeometry, floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = -2;
      floor.receiveShadow = true;
      scene.add(floor);
    }
    
    // Animation function
    const animate = () => {
      const animationId = requestAnimationFrame(animate);
      
      // Update orbit controls
      if (controlsRef.current) {
        controlsRef.current.autoRotate = isRotating;
        controlsRef.current.update();
      }
      
      // Update animation mixer if available
      if (mixerRef.current) {
        const delta = clockRef.current.getDelta();
        mixerRef.current.update(delta);
      }
      
      renderer.render(scene, camera);
      
      return () => {
        cancelAnimationFrame(animationId);
      };
    };
    
    // Start animation
    const cleanupAnimation = animate();
    
    // Cleanup function
    return () => {
      cleanupAnimation();
      
      // Dispose all resources
      if (object3D) {
        scene.remove(object3D);
        if (object3D.geometry) object3D.geometry.dispose();
        if (object3D.material) {
          if (Array.isArray(object3D.material)) {
            object3D.material.forEach(material => material.dispose());
          } else {
            object3D.material.dispose();
          }
        }
      }
      
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      
      scene.children.forEach((object) => {
        if (object.geometry) object.geometry.dispose();
        
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      renderer.dispose();
      
      if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [product, isRotating]);
  
  return (
    <div className="nft-3d-viewer">
      <div className="nft-3d-controls">
        <button onClick={() => setIsRotating(!isRotating)}>
          {isRotating ? 'Pause Rotation' : 'Resume Rotation'}
        </button>
        <button onClick={onClose}>Close 3D View</button>
      </div>
      
      <div className="nft-3d-container" ref={mountRef}>
        {!modelLoaded && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading 3D Model... {loadingProgress}%</div>
          </div>
        )}
      </div>
      
      <div className="nft-3d-info">
        <h3>{product.title}</h3>
        <p>{product.price} ETH</p>
        {product.model && (
          <span className="model-badge">
            {product.model.type.toUpperCase()} Model
          </span>
        )}
      </div>
    </div>
  );
};

// ProductGrid Component (unchanged structure, but using updated MythidverseViewer)
const ProductGrid = ({ products }) => {
  const [selected3DNFT, setSelected3DNFT] = useState(null);
  const [selectedMythidverseNFT, setSelectedMythidverseNFT] = useState(null);
  
  // Function to get rarity class for styling
  const getRarityClass = (rarity) => {
    switch(rarity) {
      case 'common':
        return 'rarity-common';
      case 'uncommon':
        return 'rarity-uncommon';
      case 'rare':
        return 'rarity-rare';
      case 'epic':
        return 'rarity-epic';
      case 'legendary':
        return 'rarity-legendary';
      default:
        return 'rarity-common';
    }
  };
  
  // Toggle 3D view (kept for backward compatibility)
  const toggle3DView = (event, product) => {
    event.preventDefault();
    setSelected3DNFT(product);
  };
  
  // Toggle Mythidverse view
  const toggleMythidverseView = (event, product) => {
    event.preventDefault();
    setSelectedMythidverseNFT(product);
  };

  return (
    <>
      {selected3DNFT && (
        <div className="nft-3d-modal">
          <div className="nft-3d-modal-content">
            <NFTCard3D 
              product={selected3DNFT} 
              onClose={() => setSelected3DNFT(null)} 
            />
          </div>
        </div>
      )}
      
      {selectedMythidverseNFT && (
        <div className="mythidverse-modal">
          <div className="mythidverse-modal-content">
            <MythidverseViewer 
              product={selectedMythidverseNFT} 
              onClose={() => setSelectedMythidverseNFT(null)} 
            />
          </div>
        </div>
      )}
    
      <div className="product-grid">
        {products.length > 0 ? (
          products.map((product) => (
            <div className="product-card-container" key={product.id}>
              <Link to={`/nft/${product.id}`} style={{ textDecoration: 'none' }}>
                <div className="product-card">
                  <span className={`rarity-badge ${getRarityClass(product.rarity)}`}>
                    {product.rarity.charAt(0).toUpperCase() + product.rarity.slice(1)}
                  </span>
                  {product.model && (
                    <span className="model-badge">3D</span>
                  )}
                  <img
                    src={product.image}
                    alt={product.title}
                    className="product-image"
                  />
                  <div className="product-details">
                    <h3 className="product-title">{product.title}</h3>
                    <div className="product-creator">
                      <img
                        src={product.creator.avatar}
                        alt={product.creator.name}
                        className="creator-avatar"
                      />
                      <span className="creator-name">{product.creator.name}</span>
                    </div>
                    <div className="product-price">
                      <span className="price-value">{product.price} ETH</span>
                    </div>
                  </div>
                </div>
              </Link>
              <button 
                className="view-mythidverse-button" 
                onClick={(e) => toggleMythidverseView(e, product)}
              >
                View in Mythidverse
              </button>
            </div>
          ))
        ) : (
          <p>No products found</p>
        )}
      </div>
    </>
  );
};

export default ProductGrid;